import { useRef, useState } from "react";
import api from "@/lib/api";
import { assetUrl } from "@/lib/forms";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

const MAX = 20 * 1024 * 1024;
const EDGE = 2400;

async function shrink(file) {
  if (file.type === "image/gif") return file;
  const img = await createImageBitmap(file);
  const k = Math.min(1, EDGE / Math.max(img.width, img.height));
  if (k === 1 && file.size < 1.5 * 1024 * 1024) return file;
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  const type = file.type === "image/png" ? "image/png" : "image/webp";
  const blob = await new Promise((r) => c.toBlob(r, type, 0.86));
  return new File([blob], file.name.replace(/\.\w+$/, type === "image/png" ? ".png" : ".webp"), { type });
}

export default function ImageInput({ value, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX) { toast.error("Image must be under 20 MB"); return; }
    setBusy(true);
    try {
      const f = await shrink(file);
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/assets", fd, { timeout: 120000 });
      onChange(data.url);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-2">
      {value && <img src={assetUrl(value)} alt="" className="h-8 w-12 object-contain rounded border bg-[#111]" />}
      <input className="flex-1 min-w-0 px-2.5 py-1.5 rounded border text-xs font-mono" placeholder="https://… or upload" value={value || ""}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      <button type="button" className="btn shrink-0 px-2" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testid}-upload`}><Upload size={13} />{busy ? "…" : ""}</button>
      {value && <button type="button" className="btn-ghost p-1" onClick={() => onChange("")} data-testid={`${testid}-clear`}><X size={13} /></button>}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-file`} />
    </div>
  );
}
